import Cookies from 'js-cookie';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import CircleCheckLogo from '../../../assets/img/circle-check.svg';
import envConfig from '../../../config';
import { ApplicationApi, SectionOutro } from '../../../openapi/atlantis';
import {
  OnboardingResponseRoleEnum,
  OnboardingResponseStatusEnum,
} from '../../../openapi/yenta/api.ts';
import ErrorService from '../../../services/ErrorService.ts';
import { refetchAuthUserDetail } from '../../../slices/AuthSlice.ts';
import { useAppDispatch } from '../../../slices/store';
import { PartialEnumMap } from '../../../types.ts';
import { cn } from '../../../utils/classUtils';
import { ApplicationStepWithNewContent } from '../../../utils/ConfigDrivenFormUtils';
import { safePartialEnumMapGet } from '../../../utils/EnumHelper.ts';
import Logger from '../../../utils/Logger';
import { getSherlockConfiguration } from '../../../utils/OpenapiConfigurationUtils';
import Confetti from '../../Confetti.tsx';
import DefaultSplashV2 from '../../DefaultSplashV2';
import ConfigDrivenContainerLayoutV2 from '../../Layout/ConfigDrivenContainerLayoutV2.tsx';

interface ConfigDrivenOnboardingOutroContainerV2Props {
  config: ApplicationStepWithNewContent<SectionOutro>;
}

const ConfigDrivenOnboardingOutroContainerV2 = ({
  config,
}: ConfigDrivenOnboardingOutroContainerV2Props) => {
  const { applicationId } = useParams();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [applicationStatus, setApplicationStatus] = useState<
    OnboardingResponseStatusEnum | undefined
  >(undefined);

  const { id } = config.content || {};

  const titleMapping: PartialEnumMap<OnboardingResponseStatusEnum, string> = {
    STARTED: "We've received your application!",
    PENDING_APPROVAL: "We've received your application!",
    APPROVED: 'Your application was approved!',
    REJECTED: 'Your application was not approved.',
  };

  const applicationStatusMapping: PartialEnumMap<
    OnboardingResponseStatusEnum,
    string
  > = {
    STARTED: 'Application under review',
    PENDING_APPROVAL: 'Application under review',
    APPROVED: 'Application approved',
    REJECTED: 'Application rejected',
  };

  const statusMessagesMapping: PartialEnumMap<
    OnboardingResponseStatusEnum,
    React.ReactNode
  > = {
    STARTED:
      "Your account approval is pending. We'll notify you once it's approved.",
    PENDING_APPROVAL:
      "Your account approval is pending. We'll notify you once it's approved.",
    APPROVED: (
      <>
        You can now log in with your credentials at{' '}
        <a
          href={envConfig.boltBaseUrl}
          className='text-reskin-primary-blue font-bolder'
        >
          {envConfig.boltBaseUrl}
        </a>
      </>
    ),
  };

  const handleAgentUserRedirection = useCallback(async () => {
    const { userData } = await dispatch(refetchAuthUserDetail(true));
    const agentOnboardingData = userData?.onboardings?.find(
      (onboarding) => onboarding.role === OnboardingResponseRoleEnum.Agent,
    );
    if (agentOnboardingData) {
      const agentOnboardingBasePath =
        !!agentOnboardingData.sherlockApplicationId
          ? envConfig.reactAppHostUrl
          : envConfig.boltBaseUrl;
      const redirectParam = !!agentOnboardingData.sherlockApplicationId
        ? '&redirectTo=agent-onboarding'
        : '';
      window.location.assign(
        `${agentOnboardingBasePath}/redirect?jwtToken=${Cookies.get(
          'jwtToken',
        )}${redirectParam}`,
      );
    } else {
      const onboardingApplication = userData?.onboardings?.find(
        (application) =>
          application.role ===
          OnboardingResponseRoleEnum.TransactionCoordinator,
      );
      setApplicationStatus(onboardingApplication?.status);
      setIsLoading(false);
    }
  }, [dispatch]);

  const submitOnboardingApplication = useCallback(async () => {
    try {
      await new ApplicationApi(
        await getSherlockConfiguration(),
      ).submitApplication(applicationId!);
      await handleAgentUserRedirection();
    } catch (e: any) {
      Logger.error('Failed to submit application:', e);
      ErrorService.notify('Failed to submit application', e);
    }
  }, [applicationId, handleAgentUserRedirection]);

  useEffect(() => {
    setIsLoading(true);
    submitOnboardingApplication();
  }, [submitOnboardingApplication]);

  if (isLoading) {
    return (
      <div className='h-screen w-screen'>
        <DefaultSplashV2 />
      </div>
    );
  }

  return (
    <ConfigDrivenContainerLayoutV2
      config={{
        ...config,
        sectionName: safePartialEnumMapGet(
          applicationStatusMapping,
          applicationStatus!,
          'Application under review',
        ),
      }}
    >
      <div
        key={id}
        className='flex flex-col flex-grow justify-center items-center'
      >
        <div className='flex flex-col items-center justify-center relative'>
          <img
            src={CircleCheckLogo}
            alt='circle-check'
            className='w-[120px] h-[120px]'
          />
          <p
            className={cn(
              'font-poppins-semibold text-2xl font-semibold mt-4 text-center',
              applicationStatus === OnboardingResponseStatusEnum.Rejected
                ? 'text-primary-coral'
                : 'text-reskin-primary-dark',
            )}
          >
            {safePartialEnumMapGet(
              titleMapping,
              applicationStatus!,
              "We've received your application!",
            )}
          </p>
          <p className='font-inter-regular font-normal text-base text-reskin-grey-600 text-center mt-4'>
            {safePartialEnumMapGet(
              statusMessagesMapping,
              applicationStatus!,
              "Your account approval is pending. We'll notify you once it's approved.",
            )}
          </p>
          <Confetti
            customImgClassName='w-96 h-96'
            customContainerClassName='!w-full !h-full !right-0'
          />
        </div>
      </div>
    </ConfigDrivenContainerLayoutV2>
  );
};

export default ConfigDrivenOnboardingOutroContainerV2;
